<script lang="ts">
import { defineComponent } from 'vue'
export default defineComponent({
  name: 'base-download-button',
})
</script>
<template>
  <el-button
    v-blur
    :type="type"
    :link="link"
    :size="size"
    :plain="plain"
    :round="round"
    :circle="circle"
    :disabled="disabled"
    :icon="icon"
    :loading="showLoading && loading"
    @click="handleDownload"
  >
    {{ label }}
  </el-button>
</template>

<script lang="ts" setup name="base-download-button">
  import { ref, PropType } from 'vue';
  import { exportRequestUrl, downloadByUrlRequest, downloadByStreamRequest } from '@/api/modules/common';
  import { ElMessage } from 'element-plus';
  import type { EpPropMergeType } from 'element-plus/es/utils/vue/props/types';

  const props = defineProps({
    /**
     * 说明: 下载接口请求地址
     * 类型: string
     * 可选值: -
     * 默认值: 默认通用异步导出功能
     */
    request: {
      type: String,
      default: exportRequestUrl,
    },
    /**
     * 说明: 接口是以什么类型返回数据的
     * 类型: number
     * 可选值: 1 | 2 | 3；  1 - 接口返回下载地址；2 - 接口返回stream文档流；3 - 接口返回JSON文本
     * 默认值: 1
     */
    resType: {
      type: Number,
      default: 1,
    },
    /**
     * 说明: 请求参数，结合请求接口传递对应请求参数
     * 类型: 任意类型
     * 可选值: -
     * 默认值: -
     */
    queryParams: {
      type: Object,
      default: () => {},
    },
    /**
     * 说明: 请求方式
     * 类型: string
     * 可选值: get/post
     * 默认值: get
     */
    method: {
      type: String,
      default: 'get',
    },
    /**
     * 说明: 按钮展示给用户的问题
     * 类型: string
     * 可选值: -
     * 默认值: 下载
     */
    label: {
      type: String,
      default: '下载',
    },
    /**
     * 说明: 类型
     * 类型: string
     * 可选值: 'primary'| 'success'| 'warning'| 'danger'| 'info'| 'text'(delete)
     * 默认值: primary
     */
    type: {
      type: String as PropType<
        EpPropMergeType<StringConstructor, 'primary'|'success'|'warning'|'danger'|'info', unknown>
      >,
      default: '',
    },
    /**
     * 说明: 尺寸
     * 类型: string
     * 可选值: large / small / default
     * 默认值: default
     */
    size: {
      type: String as PropType<
        EpPropMergeType<StringConstructor, 'large'|'small'|'default', unknown>
      >,
      default: '',
    },
    /**
     * 说明: 是否为链接按钮，去掉以前的type='text'
     * 类型: boolean
     * 可选值: -
     * 默认值: false
     */
    link: {
      type: Boolean,
      default: false,
    },
    /**
     * 说明: 是否朴素按钮
     * 类型: boolean
     * 可选值: -
     * 默认值: false
     */
    plain: {
      type: Boolean,
      default: false,
    },
    /**
     * 说明: 是否圆角按钮
     * 类型: boolan
     * 可选值: -
     * 默认值: false
     */
    round: {
      type: Boolean,
      default: false,
    },
    /**
     * 说明: 是否圆形按钮
     * 类型: boolan
     * 可选值: -
     * 默认值: false
     */
    circle: {
      type: Boolean,
      default: false,
    },
    /**
     * 说明: 是否禁用状态
     * 类型: boolan
     * 可选值: -
     * 默认值: false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 说明: 图标类名
     * 类型: string
     * 可选值: -
     * 默认值: -
     */
    icon: {
      type: String,
      default: '',
    },
    /**
     * 说明: 是否在按钮前边展示loading
     * 类型: boolean
     * 可选值: true / false
     * 默认值: small
     */
    showLoading: {
      type: Boolean,
      default: true,
    },
    /**
     * 说明: 文件下载名称
     * 类型: string
     * 可选值: -
     * 默认值: -
     */
    downloadName: {
      type: String,
      default: '',
    },
    /**
     * 说明: 文件下载类型
     * 类型: string
     * 可选值:
     * application/vnd.ms-excel（后缀为xls，office打开时总提示文件类型和文件内容不符问题）
     * 或者 application/vnd.openxmlformats-officedocument.spreadsheetml.sheet（后缀为xlsx）
     * 默认值: application/vnd.openxmlformats-officedocument.spreadsheetml.shee
     */
    blobType: {
      type: String,
      default: 'application/vnd.openxmlformats-officedocument.spreadsheetml.shee',
    },
    /**
     * 说明: 默认异步导出必传code，用于确定是哪一个业务模块的导出
     * 类型: string | number
     * 可选值: -
     * 默认值: -
     */
    typeCode: {
      type: [String, Number],
      default: '',
    },
    isAsync: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['on-success']);

  const loading = ref(false);

  const downloadSuccess = () => {
    // 0 -- 成功   -1 -- 失败
    setTimeout(() => {
      emit('on-success', 0, '下载成功');
    }, 200);
  };

  const handleDownloadByUrl = () => {
    const isAsyncExport = props.request === exportRequestUrl;
    let queryParams = props.queryParams;
    let method = props.method;
    if (isAsyncExport && props.typeCode) {
      queryParams = {
        typeCode: props.typeCode,
        bizParam: props.queryParams,
      };
      method = 'post';
    } else if (isAsyncExport && !props.typeCode) {
      ElMessage({
        message: '通用异步导出，typeCode为参数必传！',
        type: 'warning',
        grouping: true,
      });
      return;
    }

    loading.value = true;
    downloadByUrlRequest(props.request, queryParams, method).then((res) => {
      if (res.code === 0) {
        if (isAsyncExport || props.isAsync) {
          ElMessage({
            message: '操作成功，请在系统右上角「下载中心」模块查看！',
            // message: `${res.msg || '操作成功，请在系统右上角「下载中心」模块查看！'}`,
            type: 'success',
            grouping: true,
          });
        } else {
          if (!res.data) {
            const errorMsg = res.msg || '服务器异常，请联系系统管理员!';
            ElMessage({
              message: errorMsg,
              type: 'error',
              grouping: true,
            });
            return;
          }
          const aEle = document.createElement('a');
          aEle.href = res.data;
          document.body.appendChild(aEle);
          aEle.click();
          document.body.removeChild(aEle);
        }
        downloadSuccess();
      } else {
        const errorMsg = res.msg || '服务器异常，请联系系统管理员!';
        ElMessage({
          message: errorMsg,
          type: 'error',
          grouping: true,
        });
      }
    }).finally(() => {
      loading.value = false;
    });
  };

  const handleDownloadByStream = () => {
    loading.value = true;
    downloadByStreamRequest(props.request, props.queryParams, props.method).then((res) => {
      if (res) {
        console.log('🚀 ~ downloadByStreamRequest ~ res:', res);
        const blob = new Blob([res.data], { type: props.blobType });
        const aEle = document.createElement('a');
        aEle.href = URL.createObjectURL(blob);
        if (props.downloadName) {
          aEle.download = props.downloadName;
        } else {
          aEle.download = (res.headers['content-disposition']
            || res.headers['Content-Disposition'])!.split('filename=')[1] || '表具.xlsx';
        }
        document.body.appendChild(aEle);
        aEle.click(); // 点击下载
        document.body.removeChild(aEle); // 下载完成移除元素
        window.URL.revokeObjectURL(aEle.href); // 释放掉blob对象
        downloadSuccess();
      } else {
        const errorMsg = res.msg || '服务器异常，请联系系统管理员!';
        ElMessage({
          message: errorMsg,
          type: 'error',
          grouping: true,
        });
      }
    }).finally(() => {
      loading.value = false;
    });
  };

  const handleDownloadByJSON = () => {
    loading.value = true;
    downloadByStreamRequest(props.request, props.queryParams, props.method).then((res) => {
      if (res) {
        const downloadName = props.downloadName ? props.downloadName : '';
        // 对IE和Edge的兼容
        if ('msSaveBlob' in window.navigator) {
          (window as any).navigator.msSaveBlob(res, downloadName);
        } else if (window.FileReader) {
          const reader = new FileReader();
          reader.readAsDataURL(res);
          reader.onload = (e) => {
            const aEle:any = document.createElement('a');
            if (downloadName) {
              aEle.download = downloadName;
            }
            aEle.href = e.target?.result;
            document.body.appendChild(aEle);
            aEle.click();
            document.body.removeChild(aEle);
          };
        } else {
          ElMessage({
            message: '浏览器不支持，请更换或升级浏览',
            type: 'error',
            grouping: true,
          });
        }
      } else {
        const errorMsg = res.msg || '服务器异常，请联系系统管理员!';
        ElMessage({
          message: errorMsg,
          type: 'error',
          grouping: true,
        });
      }
    }).finally(() => {
      loading.value = false;
    });
  };

  const handleDownload = () => {
    switch (props.resType) {
    case 1:
      handleDownloadByUrl();
      break;
    case 2:
      handleDownloadByStream();
      break;
    case 3:
      handleDownloadByJSON();
      break;

    default:
      ElMessage.error('无对应下载类型');
      break;
    }
  };
</script>
